import { SxProps, Theme } from '@mui/system';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Note } from 'sr-types/lib/search/v1/graphql';
import type { EntityName, EntitySecurity } from '../auth/Security';
import ResponsiveActions from '../components/ResponsiveActions';
import { FORM_BUTTONS } from '../form/formConstants';
import { useOnMobile } from '../hooks/breakpoints';
import { useIsWithinBreakpoints } from '../hooks/useIsWithinBreakpoints';
import { H, V } from '../Layout';
import LexicaljsEditor from '../lexical/LexicaljsEditor';
import { useStyling } from '../Theme';
import EmptyPromise from '../utils/EmptyPromise';
import LexicalRendererFooter from './LexicalRendererFooter';
import LexicalRendererHeader from './LexicalRendererHeader';
import { useLexicalRendererActions } from './useLexicalRendererActions';

type LexicalRendererProps = {
    contentString: string;
    isMarkdownContent?: boolean;
    contentPlaceholder: string;
    /** Parent entity — required for hover actions (ABAC). Display-only call sites omit this. */
    entityName?: EntityName;
    /** Row/entity security for the parent — used with `entityName` for COMMENT/EDIT gates. */
    entitySecurity?: EntitySecurity;
    className: string;
    note?: Note;
    id?: string;
    collapsed?: boolean;
    onSave?: (title: string, content: string) => Promise<any>;
    onDelete?: (id: string) => Promise<any>;
    onAssociateStatusNote?: {
        saveFunction?: (note: Note) => void;
        loadingState?: boolean;
        statusNote?: Note;
    };
    showHeader?: boolean;
    showFooter?: boolean;
    title?: string;
    createdOn?: string;
    updatedOn?: string;
    createdByName?: string;
    updatedByName?: string;
    responsiveActionContainerSx?: SxProps;
    allowEmpty?: boolean;
    sx?: SxProps<Theme>;
    footerSx?: SxProps<Theme>;
    onChange?: (content: string, title?: string) => void;
    onEdit?: (note: Note) => void;
    onShare?: () => void;
    CustomFooterComponent?: React.ReactNode;
    resetEditor?: boolean;
    isEditable?: boolean;
    hideActions?: string[];
    addPendingNoteAction?: (tempId: string, cb: (noteId: string) => void) => void;
    disableAllActions?: boolean;
    maxLines?: number;
    enableMaxLineClamp?: boolean;
    disableTranslation?: boolean;
};

export default function LexicalRenderer({
    note,
    id,
    contentString,
    isMarkdownContent = false,
    contentPlaceholder,
    collapsed = false,
    onSave,
    onDelete,
    onAssociateStatusNote,
    entityName,
    entitySecurity,
    showHeader = true,
    showFooter = true,
    title = '',
    updatedOn = '',
    updatedByName = '',
    className = '',
    responsiveActionContainerSx = {},
    allowEmpty,
    sx = {},
    footerSx,
    onChange,
    onEdit,
    onShare,
    CustomFooterComponent = <></>,
    resetEditor = false,
    isEditable = false,
    hideActions = [],
    addPendingNoteAction,
    disableAllActions = false,
    maxLines,
    enableMaxLineClamp = false,
    disableTranslation = false
}: LexicalRendererProps) {
    const { theme } = useStyling();

    const noteId = note?.reference?.id || '';

    const [isHover, setIsHover] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClicked, setIsClicked] = useState(false);
    const [content, setContent] = useState(contentString);
    const [expanded, setExpanded] = useState(false);

    const revertToContent = useCallback(() => {
        setContent(contentString);
    }, [contentString]);

    const { responsiveActionsProps } = useLexicalRendererActions({
        note,
        noteId,
        entityName,
        entitySecurity,
        onSave,
        onEdit,
        onShare,
        onDelete,
        onAssociateStatusNote,
        addPendingNoteAction,
        disableAllActions,
        enableMaxLineClamp,
        isEditing,
        expanded,
        setIsHover,
        setIsEditing,
        setExpanded,
        revertToContent
    });

    useEffect(() => {
        setContent(contentString);
    }, [contentString]);

    useEffect(() => {
        setIsEditing(isEditable);
    }, [isEditable]);

    const isMobile = useOnMobile();
    const isTablet = useIsWithinBreakpoints(['s', 'xs']);

    const onSaveHandle = useCallback(
        async (title: string, content: string) => {
            await onSave?.(title, content);
            setIsEditing(false);
            setIsHover(false);
        },
        [onSave]
    );

    const onCancelHandle = useCallback(async () => {
        setIsEditing(false);
        setIsHover(false);
        setContent(contentString);
        return EmptyPromise;
    }, [contentString]);

    const onDeleteHandle = useCallback(
        async (id: string) => {
            onDelete?.(id);
            setIsEditing(false);
            setIsHover(false);
            return EmptyPromise;
        },
        [onDelete]
    );

    const handleEditorChange = useCallback(
        (newValue: unknown, newTitle?: string) => {
            if (typeof newValue === 'string') {
                setContent(newValue);
                onChange?.(newValue, newTitle);
            }
        },
        [onChange]
    );

    return (
        <V
            className={isTablet ? `${className} isTablet` : className}
            sx={{
                position: 'relative'
            }}
            onClick={() => setIsClicked(!isClicked)}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <LexicaljsEditor
                id={noteId || id}
                contentPlaceholder={contentPlaceholder}
                resetEditor={resetEditor}
                readOnly={!isEditing}
                isSmall
                collapsed={collapsed}
                content={content}
                isMarkdownContent={isMarkdownContent}
                onChange={handleEditorChange}
                onSave={hideActions.includes(FORM_BUTTONS.save) ? undefined : onSaveHandle}
                onDelete={onDelete && !hideActions.includes(FORM_BUTTONS.delete) ? onDeleteHandle : undefined}
                onCancel={hideActions.includes(FORM_BUTTONS.cancel) ? undefined : onCancelHandle}
                header={showHeader && !isEditing && collapsed ? <LexicalRendererHeader text={title} /> : <Fragment />}
                footer={
                    showFooter && !isEditing ? (
                        <LexicalRendererFooter
                            updatedOn={updatedOn}
                            updatedByName={updatedByName}
                            CustomFooterComponent={CustomFooterComponent}
                            disableTranslation={disableTranslation}
                            content={content}
                            onTranslated={setContent}
                            sx={footerSx}
                        />
                    ) : (
                        <></>
                    )
                }
                isEditing={isEditing}
                allowEmpty={allowEmpty}
                sx={sx}
                isExpanded={expanded}
                maxLines={maxLines}
                enableMaxLineClamp={enableMaxLineClamp}
                disableTranslation={showFooter ? true : disableTranslation} // If we are displaying translate button in footer no need to display here as well.
            />

            <H
                sx={
                    isMobile
                        ? {
                              position: 'absolute',
                              right: '0px',
                              top: '0px',
                              justifyContent: 'flex-end'
                          }
                        : {
                              position: 'static'
                          }
                }
            >
                {!isEditing ? (
                    <ResponsiveActions
                        showBorder
                        {...responsiveActionsProps}
                        isActivated={isHover}
                        containerSx={{
                            backgroundColor: isMobile ? 'transparent' : theme.palette.background.paper,
                            ...responsiveActionContainerSx
                        }}
                    />
                ) : (
                    <></>
                )}
            </H>
        </V>
    );
}
